import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const AgentPortal = () => {
  const [agents, setAgents] = useState([]);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    body: ''
  });
  const agentEmailQuillRef = useRef(null);

  // Memoized Quill modules for agent email
  const agentEmailModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  }), []);

  const quillFormats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link'
  ], []);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    telephone: '',
    country: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/api/agents`);
      setAgents(response.data);
    } catch (error) {
      toast.error('Failed to fetch agents');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      if (editingAgent) {
        await axios.put(`${API}/api/agents/${editingAgent.id}`, formDataToSend);
        toast.success('Agent updated');
      } else {
        await axios.post(`${API}/api/agents`, formDataToSend);
        toast.success('Agent added');
      }

      setFormData({ full_name: '', email: '', telephone: '', country: '' });
      setShowAddAgent(false);
      setEditingAgent(null);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save agent');
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Delete this agent?')) return;
    
    try {
      await axios.delete(`${API}/api/agents/${agentId}`);
      toast.success('Agent deleted');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      full_name: agent.full_name,
      email: agent.email,
      telephone: agent.telephone,
      country: agent.country
    });
    setShowAddAgent(true);
  };

  const handleSendEmailToAll = async () => {
    if (!emailData.subject || !emailData.body) {
      toast.error('Please fill in subject and message');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/send-email-to-agents`, {
        subject: emailData.subject,
        body: emailData.body
      });
      
      toast.success(`Email sent to ${response.data.sent_count} agents`);
      setShowEmailComposer(false);
      setEmailData({ subject: '', body: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Agent Portal</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEmailComposer(true)}>
                ✉️ Email All Agents
              </Button>
              <Button onClick={() => { setShowAddAgent(!showAddAgent); setEditingAgent(null); setFormData({ full_name: '', email: '', telephone: '', country: '' }); }}>
                {showAddAgent ? 'Cancel' : '+ Add Agent'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {showAddAgent && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border rounded">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                />
              </div>
              <Button type="submit">{editingAgent ? 'Update Agent' : 'Add Agent'}</Button>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Full Name</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Telephone</th>
                  <th className="text-left p-2">Country</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.map(agent => (
                  <tr key={agent.id} className="border-b">
                    <td className="p-2">{agent.full_name}</td>
                    <td className="p-2">{agent.email}</td>
                    <td className="p-2">{agent.telephone}</td>
                    <td className="p-2">{agent.country}</td>
                    <td className="p-2 space-x-2">
                      {agent.telephone && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-green-600 hover:text-green-700 hover:border-green-600"
                          onClick={() => {
                            const phone = agent.telephone.replace(/\s+/g, '');
                            window.open(`https://wa.me/${phone}`, '_blank');
                          }}
                        >
                          📱 WhatsApp
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleEdit(agent)}>Edit</Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(agent.id)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {agents.length === 0 && (
              <p className="text-center text-gray-500 py-4">No agents yet. Add your first agent!</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Email Composer Dialog */}
      <Dialog open={showEmailComposer} onOpenChange={setShowEmailComposer}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Email to All Agents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This email will be sent to all {agents.length} active agents using your SMTP settings.
            </p>
            <div>
              <Label>Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <p className="text-xs text-gray-500 mb-2">Use {'{name}'} to personalize with agent's name</p>
              
              {/* Draggable Tags */}
              <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-xs font-semibold text-gray-700 mb-2">Available Tags (Drag & Drop):</p>
                <div className="flex gap-2 flex-wrap">
                  <span 
                    draggable 
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', '{name}')}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm cursor-move hover:bg-blue-200 transition-colors"
                  >
                    📝 {'{name}'}
                  </span>
                  <span 
                    draggable 
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', '{email}')}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm cursor-move hover:bg-green-200 transition-colors"
                  >
                    ✉️ {'{email}'}
                  </span>
                </div>
              </div>
              
              <ReactQuill
                ref={agentEmailQuillRef}
                theme="snow"
                value={emailData.body}
                onChange={(content) => setEmailData({ ...emailData, body: content })}
                style={{ height: '300px', marginBottom: '50px' }}
                modules={agentEmailModules}
                formats={quillFormats}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEmailComposer(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmailToAll}>
                Send to All Agents
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
